import i18n from '@/utils/i18n';
import React from 'react';
import { createStyles, useTheme } from '@/contexts/theme-context';
import { FlatList } from 'react-native';
import { formatCurrency } from '@/utils/money';
import { formatDateWithYear } from '@/utils/date';
import { ListItemSeparator } from '@/components/list-item';
import { Payments_GetManualDepositsQuery } from '@/types';
import { PaymentsListItem } from './payments-list-item';
import { Typography } from '@usepepper/ui/atoms/typography';

type ManualDeposit =
  Payments_GetManualDepositsQuery['getManualDeposits']['deposits'][number];

interface Props {
  currencyCode: string | null;
  deposits: ManualDeposit[];
}

/**
 * "2 cash payments + 1 check" — omits either side when its count is zero.
 */
const formatDepositContents = ({
  cashCount,
  checkCount,
}: {
  cashCount: number;
  checkCount: number;
}) => {
  const parts: string[] = [];

  if (cashCount > 0) {
    parts.push(
      cashCount === 1
        ? i18n.t('bank_deposits.cash_count.payment', { count: cashCount })
        : i18n.t('bank_deposits.cash_count.payments', { count: cashCount }),
    );
  }

  if (checkCount > 0) {
    parts.push(
      checkCount === 1
        ? i18n.t('bank_deposits.check_count.check', { count: checkCount })
        : i18n.t('bank_deposits.check_count.checks', { count: checkCount }),
    );
  }

  return parts.join(' + ');
};

export const BankDepositsList: React.FC<Props> = ({
  currencyCode,
  deposits,
}) => {
  const [styles] = useStyles();
  const theme = useTheme();

  return (
    <FlatList
      data={deposits}
      ItemSeparatorComponent={() => (
        <ListItemSeparator style={styles.separator} />
      )}
      keyExtractor={(deposit) => deposit.manual_deposit_id}
      renderItem={({ item: deposit }) => (
        <PaymentsListItem
          bottomLeft={
            <Typography
              color={theme.sk.color.text.secondary}
              variant="paragraph-sm"
            >
              {formatDepositContents({
                cashCount: deposit.cash_count,
                checkCount: deposit.check_count,
              })}
            </Typography>
          }
          bottomRight={
            <Typography
              color={theme.sk.color.text.secondary}
              variant="paragraph-sm"
            >
              {formatDateWithYear(deposit.deposit_date)}
            </Typography>
          }
          testID="bank-deposit-list-item"
          topLeft={
            <Typography
              color={theme.sk.color.text.primary}
              variant="paragraph-base-600"
            >
              {i18n.t('bank_deposits.deposit_title', {
                // The employee may or may not have typed the "#" themselves.
                depositNumber: deposit.deposit_number.replace(/^#/, ''),
              })}
            </Typography>
          }
          topRight={
            <Typography
              color={theme.sk.color.text.primary}
              variant="paragraph-base"
            >
              {formatCurrency(deposit.total_amount_in_micros, currencyCode)}
            </Typography>
          }
        />
      )}
    />
  );
};

const useStyles = createStyles((theme) => ({
  separator: {
    backgroundColor: theme.sk.color.divider.default,
    marginHorizontal: theme.sk.spacing.spaceMedium,
  },
}));
